import React, { useEffect, useState } from 'react';
import { IconBuildingStore, IconDownload, IconPlus } from '@tabler/icons-react';
import { SmartSelect } from '../dropdown/SmartSelect';
import {
  provisionCustomer,
  orderTrial,
  orderLicense,
  generateInstaller,
  listEsetProducts,
  EsetApiError,
} from '../../services/esetApi';
import type { EsetProduct, EsetProvisionResult } from '../../services/esetApi';
import { useEsetBundles } from '../../hooks/useEsetBundles';

/** A status line: `info` for success, `warn` for a transient/retryable condition
 *  (e.g. installer not ready yet), `error` for a real failure. */
type ProvMsg = { tone: 'info' | 'warn' | 'error'; text: string };

interface EsetProvisionSectionProps {
  /** The SM4RT customer's cr001_id (RepairShopr id) — provisioning is keyed on this. */
  customerId: string | null;
  /** True once the ESET company (managed customer) exists. */
  provisioned: boolean;
  /** Called after a successful provisioning action so the panel reloads. */
  onChanged: () => void;
}

export const EsetProvisionSection: React.FC<EsetProvisionSectionProps> = ({
  customerId,
  provisioned,
  onChanged,
}) => {
  const [esetProducts, setEsetProducts] = useState<EsetProduct[]>([]);
  const bundles = useEsetBundles();
  const [trial, setTrial] = useState(false);
  const [productCode, setProductCode] = useState('');
  const [bundleGuid, setBundleGuid] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [busy, setBusy] = useState<string | null>(null);
  const [msg, setMsg] = useState<ProvMsg | null>(null);

  useEffect(() => {
    let alive = true;
    listEsetProducts()
      .then((r) => alive && setEsetProducts(r.products || []))
      .catch(() => {});
    return () => {
      alive = false;
    };
  }, []);

  const run = async (label: string, fn: () => Promise<EsetProvisionResult>) => {
    if (!customerId) {
      setMsg({ tone: 'error', text: 'No customer id available' });
      return;
    }
    setBusy(label);
    setMsg(null);
    try {
      const res = await fn();
      setMsg({ tone: 'info', text: `${label}: ${res.status}` });
      onChanged();
    } catch (e) {
      // A retryable condition (e.g. the installer's deployment group hasn't
      // propagated yet) is a soft warning, not a failure — show the backend's
      // friendly message and let the operator retry in a moment.
      if (e instanceof EsetApiError && e.retryable) {
        setMsg({ tone: 'warn', text: e.message });
      } else {
        setMsg({ tone: 'error', text: e instanceof Error ? e.message : `${label} failed` });
      }
    } finally {
      setBusy(null);
    }
  };

  const submitOrder = () => {
    const input = {
      customerId: customerId as string,
      productCode,
      quantity,
      productGuid: bundleGuid || undefined,
    };
    run(trial ? 'Trial' : 'License', () => (trial ? orderTrial(input) : orderLicense(input)));
  };

  return (
    <div className="eset-card">
      <div className="eset-card__hdr">Provisioning</div>
      <div className="eset-prov">
        {msg && <div className={`eset-prov__msg eset-prov__msg--${msg.tone}`}>{msg.text}</div>}

        {!provisioned ? (
          <>
            <div className="eset-prov__hint">Not yet provisioned in ESET.</div>
            <button
              className="eset-prov__action eset-prov__action--primary"
              disabled={busy !== null || !customerId}
              onClick={() => run('Provision company', () => provisionCustomer(customerId as string))}
            >
              <IconBuildingStore size={14} stroke={1.5} /> Provision ESET company
            </button>
          </>
        ) : (
          <>
            {/* Add a license */}
            <div className="eset-prov__form">
              <div className="eset-prov__row">
                <label className="eset-prov__toggle">
                  <input type="checkbox" checked={trial} onChange={(e) => setTrial(e.target.checked)} /> Trial
                </label>
                <input
                  className="eset-prov__qty"
                  type="number"
                  min={1}
                  value={quantity}
                  onChange={(e) => setQuantity(Math.max(1, Number(e.target.value) || 1))}
                  title="Seats"
                  aria-label="Seats"
                />
              </div>
              <SmartSelect
                label="ESET product"
                value={productCode || null}
                options={esetProducts.map((p) => ({ value: String(p.code), label: p.name }))}
                onChange={(v) => setProductCode(v ?? '')}
              />
              <SmartSelect
                label="Billing bundle"
                value={bundleGuid || null}
                options={bundles.map((b) => ({ value: b.guid, label: b.sku }))}
                onChange={(v) => setBundleGuid(v ?? '')}
                placeholder={trial ? 'Optional' : 'Select…'}
              />
              <button
                className="eset-prov__action eset-prov__action--primary"
                disabled={!customerId || !productCode || busy !== null}
                onClick={submitOrder}
              >
                <IconPlus size={14} stroke={1.5} /> {trial ? 'Order trial' : 'Order license'}
              </button>
            </div>

            <button
              className="eset-prov__action"
              disabled={busy !== null || !customerId}
              onClick={() => run('Installer', () => generateInstaller(customerId as string))}
            >
              <IconDownload size={14} stroke={1.5} /> Generate installer
            </button>
          </>
        )}
      </div>
    </div>
  );
};
